import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { Platform, Linking, ActivityIndicator, Alert } from 'react-native';
import Spacer from '../../components/Spacer';
import { Octicons } from '@expo/vector-icons';
import { useAuthRequest } from 'expo-auth-session';
import {
  getFitbitAuthProps,
  getFitbitAuthRequestConfig,
  fitbitDiscovery
} from '../../tools/FitbitHelper';
import { revokeFitbitAuthorization } from '../../tools/WearableHelper';
import { GoogleFitHealth } from '../../tools/google-fit';
import { HealthKitSync } from '../../tools/health-kit';
import AsyncStorage, {
  useAsyncStorage
} from '@react-native-async-storage/async-storage';
import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { useUpdateUser } from '../../hooks/user/useUpdateUser';
import { useUserContext } from '../../context/UserContext';
import {
  DeviceSyncStatus,
  useDeviceSyncContext
} from '../../context/DeviceSyncContext';
import { UserAttributeSource } from '../../models/UserAttribute';
import { addSeconds, format } from 'date-fns';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  GarminToken,
  requestToken,
  revokeAccess
} from '../../tools/GarminHelper';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';
import { StackNavigationProp } from '@react-navigation/stack';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import { H5, I } from '../../components/Typography';
import { MyDataParamList } from '../../navigation/MyDataNav';
import Colors from '../../constants/Colors';
import Button from '../../components/Button';
import elementIDs from '../../../elementIDs';
import { getOuraAuthRequestConfig, useOura } from '../../tools/OuraHelper';
import WebViewModal from '../modals/WebViewModal';
import { GoogleHealthConnect } from '../../tools/google-health-connect';
import * as WebBrowser from 'expo-web-browser';

WebBrowser.maybeCompleteAuthSession();

const StyledContainer = styled.View`
  flex: 1;
  background-color: ${Colors.background};
`;

const StyledTitleView = styled.View`
  padding: 0 21px;
  margin: 21px 24px 0 21px;
  max-width: 768px;
  width: 100%;
  align-self: center;
`;

const StyledScrollView = styled.ScrollView<{
  backgroundColor?: string;
}>`
  ${p => (p.backgroundColor ? `background-color: ${p.backgroundColor}` : '')};
  flex: 1;
  margin: 32px 0 0 0;
`;

const StyledContentContainer = styled.View`
  flex: 1;
  width: 100%;
  align-self: center;
  max-width: 768px;
  margin-top: ${Platform.OS === 'web' ? 0 : 10}px;
  padding: 0 21px 24px 21px;
`;

const StyledSubtitle = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 16px;
  line-height: 18px;
  color: #141027;
`;

const StyledWearableContainer = styled.TouchableOpacity.attrs(() => ({
  activeOpacity: 0.8
}))`
  flex-direction: row;
  background-color: white;
  width: 100%;
  border-radius: 16px;
  border-width: 2px;
  border-color: #cfcaf6;
  align-items: center;
  padding: 12px 33px 12px 16px;
`;

const StyledWearableView = styled.View`
  flex: 1;
  flex-direction: row;
  align-items: center;
`;

const StyledLabelContainer = styled.View`
  flex: 1;
`;

const StyledLabel = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 18px;
  line-height: 20px;
  color: #232f55;
  margin-left: 14px;
`;

const StyledSubLabel = styled(StyledLabel)<{ small?: boolean }>`
  margin-top: 6px;
  font-family: 'DMSans-Regular';
  ${p => (p.small ? 'font-size: 14px;' : '')}
`;

const StyledImage = styled.Image`
  height: 65px;
  width: 67px;
  border-radius: 15px;
`;

const StyledGarminImage = styled.Image`
  height: 62px;
  width: 62px;
  border-radius: 15px;
`;

interface MyProfileWearableSettingsScreenProps {
  embedded?: boolean;
  showTitle?: boolean;
  fromMyDataScreen?: boolean;
}

const MyProfileWearableSettingsScreen: React.FC<
  MyProfileWearableSettingsScreenProps
> = ({ embedded, showTitle, fromMyDataScreen }) => {
  const { updatedUser, updateUser, clearUpdatedUser } = useUpdateUser();
  const { user, setUser, updateUserSession } = useUserContext();
  const [fitbitAccessToken, setFitbitAccessToken] = useState<
    string | undefined
  >();
  const config = getOuraAuthRequestConfig();
  const [openAndroidOuraConnect, setOpenAndroidAuraConnect] = useState(false);
  const {
    synchronizeData,
    deviceSyncInfo,
    lastSyncedState,
    googleFitState,
    clearDeviceSyncState,
    clearDeviceSyncInfo
  } = useDeviceSyncContext();
  const {
    getItem: getIsGoogleFitAuthorized,
    setItem: setIsGoogleFitAuthorized,
    removeItem: removeIsGoogleFitAuthorized
  } = useAsyncStorage('google-fit-connected');

  const [garminToken, setGarminToken] = useState<GarminToken | undefined>();
  const [googleFitLoading, setGoogleFitLoading] = useState<boolean>(false);
  const [healthConnectLoading, setHealthConnectLoading] =
    useState<boolean>(false);
  const [healthConnectAuthorized, setHealthConnectAuthorized] =
    useState<boolean>(false);
  const [fitbitLoading, setFitbitLoading] = useState<boolean>();
  const [garminLoading, setGarminLoading] = useState<boolean>();
  const [healthKitLoading, setHealthKitLoading] = useState<boolean>(false);
  const [healthKitAuthorized, setHealthKitAuthorized] =
    useState<boolean>(false);
  const [googleFitAuthorized, setGoogleFitAuthorized] =
    useState<boolean>(false);
  const [showFitbitWebDialog, setShowFitbitWebDialog] = useState(false);
  const [showOuraWebDialog, setShowOuraWebDialog] = useState(false);
  const fitbitAuthProps = getFitbitAuthProps();
  const [fitbitAuthRequest, fitbitAuthResponse, promptFibitAuthAsync] =
    useAuthRequest(
      getFitbitAuthRequestConfig(
        fitbitAuthProps.clientId,
        fitbitAuthProps.redirectUri
      ),
      fitbitDiscovery
    );

  const {
    loading: ouraLoading,
    ouraAccessToken,
    ouraAuthUrl,
    handleOuraAuthResult,
    connectOura,
    disconnectOura
  } = useOura();
  const [connectedWearableSources, setConnectedWearableSources] =
    useState<UserAttributeSource[]>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<MyDataParamList, 'WearableIntegrationScreen'>>();

  useEffect(() => {
    if (googleFitState === 'disconnected') {
      getIsGoogleFitAuthorized().then(value => {
        setGoogleFitAuthorized(value ? true : false);
      });
    }
  }, [googleFitState]);

  useFocusEffect(
    useCallback(() => {
      updateUserSession();
      if (Platform.OS === 'ios') {
        const getHealthKitAuthStatus = async () => {
          const healthkitAuth = new HealthKitSync(user?.id || 0);
          healthkitAuth.getAuthStatus().then(result => {
            if (result) {
              setHealthKitAuthorized(true);
            }
          });
        };
        getHealthKitAuthStatus().catch(err => {
          log(stringify(err));
        });
      } else if (Platform.OS === 'android') {
        setGoogleFitLoading(true);
        getIsGoogleFitAuthorized().then(value => {
          setGoogleFitAuthorized(value ? true : false);
          setGoogleFitLoading(false);
        });
        const healthConnect = new GoogleHealthConnect(user?.id || 0);
        healthConnect.authorize().then(initialized => {
          if (initialized) {
            setHealthConnectLoading(true);
            healthConnect.getGrantedPermissions().then(p => {
              if (p.length !== 0) {
                setHealthConnectAuthorized(true);
              }
              setHealthConnectLoading(false);
            });
          }
        });
      }
    }, [updateUserSession])
  );

  useEffect(() => {
    setFitbitLoading(false);
    if (fitbitAuthResponse?.type === 'success') {
      const { access_token } = fitbitAuthResponse.params;
      setFitbitAccessToken(access_token);
      updateUser({ fitbitAccessToken: access_token });
      if (fitbitAuthResponse.authentication?.expiresIn) {
        AsyncStorage.setItem(
          'fitbitTokenExpiration',
          addSeconds(new Date(), fitbitAuthResponse.authentication.expiresIn)
            .getTime()
            .toString()
        );
      }
    }
  }, [fitbitAuthResponse]);

  useEffect(() => {
    setFitbitAccessToken(user?.fitbitAccessToken);
    const garminConfig = user?.garminConfig;
    setGarminToken(
      garminConfig
        ? {
            key: garminConfig.accessToken,
            secret: garminConfig.accessTokenSecret
          }
        : undefined
    );
  }, [user?.fitbitAccessToken, user?.garminConfig]);

  useEffect(() => {
    // Handle initial sync upon connecting fitbit account
    if (fitbitAuthResponse?.type === 'success' && user?.fitbitAccessToken) {
      synchronizeData(UserAttributeSource.Fitbit);
    }
  }, [fitbitAuthResponse, synchronizeData, user]);

  useEffect(() => {
    if (updatedUser) {
      clearUpdatedUser();
      setUser(updatedUser);
    }
  }, [clearUpdatedUser, setUser, updatedUser]);

  const disableButtons = useMemo(
    () =>
      fitbitLoading ||
      googleFitLoading ||
      healthKitLoading ||
      garminLoading ||
      healthConnectLoading ||
      embedded,
    [
      embedded,
      fitbitLoading,
      garminLoading,
      googleFitLoading,
      healthKitLoading,
      healthConnectLoading
    ]
  );

  const getDateUpdatedString = (date?: Date | null) => {
    if (date) {
      return `Updated: ${format(new Date(date), 'MM/d/yyyy h:mm a')}`;
    }
    return 'Manage';
  };

  const checkConnectedWearables = useCallback(async () => {
    const sources: UserAttributeSource[] = [];
    if (embedded) {
      setConnectedWearableSources(
        Object.values(UserAttributeSource).map(source => source)
      );
      return;
    }
    if (user?.id) {
      if (Platform.OS === 'ios') {
        const healthKit = new HealthKitSync(user.id);
        const connected = await healthKit.getAuthStatus();
        if (connected) sources.push(UserAttributeSource.HealthKit);
      } else if (Platform.OS === 'android') {
        const googleFit = new GoogleFitHealth(user.id);
        const healthConnect = new GoogleHealthConnect(user.id);
        const connected = await googleFit.getIsGoogleFitAuthorized();
        if (connected) sources.push(UserAttributeSource.GoogleFit);
        const initialized = await healthConnect.authorize();
        if (initialized) {
          const permissions = await healthConnect.getGrantedPermissions();
          if (permissions.length > 0) {
            sources.push(UserAttributeSource.HealthConnect);
          }
        }
      }
      if (user.fitbitAccessToken) {
        sources.push(UserAttributeSource.Fitbit);
      }
      if (garminToken) {
        sources.push(UserAttributeSource.Garmin);
      }
      if (user.ouraAccessToken) {
        sources.push(UserAttributeSource.Oura);
      }
    }
    setConnectedWearableSources(sources);
  }, [
    embedded,
    user?.id,
    user?.fitbitAccessToken,
    user?.ouraAccessToken,
    garminToken
  ]);

  useEffect(() => {
    checkConnectedWearables();
  }, [checkConnectedWearables, user]);

  const [disableAutoNavigate, setDisableAutoNavigate] = useState<boolean>();

  useEffect(() => {
    if (
      !navigation.isFocused() ||
      !fromMyDataScreen ||
      route.params?.disableAutoNavigate ||
      (disableAutoNavigate ?? false)
    ) {
      return;
    }
    if (connectedWearableSources && connectedWearableSources.length > 0) {
      if (embedded) {
        navigation.replace('MyDataNavigator', {
          screen: 'MyDataListScreen',
          params: { isEmbedded: true }
        });
      } else {
        navigation.replace('DrawerNavigator', {
          screen: 'BottomTabNav',
          params: {
            screen: 'MyDataNav',
            params: { screen: 'MyDataListScreen' }
          }
        });
      }
    } else if (connectedWearableSources) {
      // This is to prevent automatic navigation to My Data screen
      // when no connected wearables was found upon initial check
      setDisableAutoNavigate(true);
    }
  }, [
    connectedWearableSources,
    disableAutoNavigate,
    embedded,
    fromMyDataScreen,
    navigation,
    route.params?.disableAutoNavigate
  ]);

  const loading = useMemo(() => {
    return (
      !connectedWearableSources ||
      (fromMyDataScreen &&
        !route.params?.disableAutoNavigate &&
        connectedWearableSources.length > 0 &&
        !disableAutoNavigate)
    );
  }, [
    connectedWearableSources,
    disableAutoNavigate,
    fromMyDataScreen,
    route.params?.disableAutoNavigate
  ]);

  useFocusEffect(
    useCallback(() => {
      clearDeviceSyncInfo();
    }, [clearDeviceSyncInfo])
  );

  return loading ? (
    <ActivityIndicatorScreen backgroundColor={Colors.background} />
  ) : (
    <StyledContainer>
      {showTitle && (
        <StyledTitleView>
          <H5 color='dark'>Wearable Integrations</H5>
        </StyledTitleView>
      )}
      <StyledScrollView
        backgroundColor={embedded ? undefined : Colors.background}
        contentContainerStyle={{ flexGrow: 1 }}
        bounces={false}
      >
        <StyledContentContainer>
          <StyledSubtitle>
            Allow Chloe to connect to your wearables and provide more accurate
            insights.
          </StyledSubtitle>
          <Spacer size={4} />
          <>
            <StyledWearableContainer
              disabled={disableButtons}
              testID={elementIDs.fitbitButtonID}
              onPress={() => {
                setFitbitLoading(true);
                if (
                  (deviceSyncInfo?.status === DeviceSyncStatus.Failed &&
                    deviceSyncInfo.message ===
                      'Fitbit account not connected.') ||
                  deviceSyncInfo?.message ===
                    'Fitbit account connection has expired.'
                ) {
                  if (user && user.fitbitAccessToken) {
                    clearDeviceSyncState(UserAttributeSource.Fitbit);
                    setFitbitAccessToken(undefined);
                    setFitbitLoading(false);
                    setUser({ ...user, fitbitAccessToken: undefined });
                    promptFibitAuthAsync();
                    return;
                  }
                }
                if (fitbitAccessToken) {
                  if (Platform.OS !== 'web') {
                    Alert.alert('Fitbit', 'Connected', [
                      {
                        text: 'Sync',
                        onPress: () => {
                          synchronizeData(UserAttributeSource.Fitbit);
                          setFitbitLoading(false);
                        }
                      },
                      {
                        text: 'Disconnect',
                        onPress: () => {
                          if (user) {
                            revokeFitbitAuthorization(
                              user.id,
                              fitbitAuthProps.clientId
                            )
                              ?.then(() => {
                                setFitbitAccessToken(undefined);
                                setFitbitLoading(false);
                                updateUserSession();
                                AsyncStorage.removeItem(
                                  'fitbitTokenExpiration'
                                );
                                AsyncStorage.removeItem('wearableConfigs');
                              })
                              ?.catch(() => {
                                setFitbitAccessToken(undefined);
                                setFitbitLoading(false);
                                updateUserSession();
                                AsyncStorage.removeItem(
                                  'fitbitTokenExpiration'
                                );
                                AsyncStorage.removeItem('wearableConfigs');
                              });
                          }
                        }
                      },
                      {
                        text: 'Cancel',
                        onPress: () => {
                          setFitbitLoading(false);
                        }
                      }
                    ]);
                  } else {
                    setShowFitbitWebDialog(true);
                  }
                } else {
                  promptFibitAuthAsync();
                }
              }}
            >
              <StyledWearableView>
                <StyledImage
                  source={require('../../assets/wearable/icon_fitbit.png')}
                />
                <StyledLabelContainer>
                  <StyledLabel>Fitbit</StyledLabel>
                  <StyledSubLabel
                    small={Boolean(fitbitAccessToken && lastSyncedState.fitbit)}
                  >
                    {fitbitAccessToken
                      ? deviceSyncInfo?.deviceKey ===
                          UserAttributeSource.Fitbit && deviceSyncInfo?.message
                        ? deviceSyncInfo?.message
                        : getDateUpdatedString(lastSyncedState.fitbit)
                      : 'Connect'}
                  </StyledSubLabel>
                </StyledLabelContainer>
                {fitbitLoading ? (
                  <ActivityIndicator />
                ) : (
                  <Octicons name='chevron-right' size={24} color='#232F55' />
                )}
              </StyledWearableView>
            </StyledWearableContainer>
          </>
          <Spacer size={3} />
          <>
            <StyledWearableContainer
              disabled={disableButtons}
              testID={elementIDs.garminButtonID}
              onPress={() => {
                setGarminLoading(true);
                if (garminToken) {
                  revokeAccess(garminToken)
                    .then(() => {
                      setGarminToken(undefined);
                      setGarminLoading(false);
                      AsyncStorage.removeItem('wearableConfigs');
                    })
                    .catch(() => {
                      setGarminLoading(false);
                      AsyncStorage.removeItem('wearableConfigs');
                    });
                } else {
                  requestToken()
                    ?.then(garminToken => {
                      setGarminToken(garminToken);
                      setGarminLoading(false);
                    })
                    ?.catch(() => setGarminLoading(false));
                }
              }}
            >
              <StyledWearableView>
                <StyledGarminImage
                  source={require('../../assets/wearable/icon_garmin.png')}
                />
                <StyledLabelContainer>
                  <StyledLabel>Garmin</StyledLabel>
                  <StyledSubLabel>
                    {garminToken ? 'Disconnect' : 'Connect'}
                  </StyledSubLabel>
                </StyledLabelContainer>
                {garminLoading ? (
                  <ActivityIndicator />
                ) : (
                  <Octicons name='chevron-right' size={24} color='#232F55' />
                )}
              </StyledWearableView>
            </StyledWearableContainer>
          </>
          <Spacer size={3} />
          {Platform.OS !== 'web' &&
            (Platform.OS === 'ios' ? (
              <>
                <StyledWearableContainer
                  disabled={disableButtons}
                  onPress={async () => {
                    setHealthKitLoading(true);
                    if (healthKitAuthorized) {
                      Alert.alert('Apple Health', 'Connected', [
                        {
                          text: 'Sync',
                          onPress: () => {
                            synchronizeData(UserAttributeSource.HealthKit);
                            setHealthKitLoading(false);
                          }
                        },
                        {
                          text: 'Permissions',
                          onPress: async () => {
                            await Linking.openURL(
                              'App-Prefs:HEALTH&path=SOURCES_ITEM'
                            );
                            setHealthKitLoading(false);
                          }
                        },
                        {
                          text: 'Cancel',
                          onPress: () => {
                            setHealthKitLoading(false);
                          }
                        }
                      ]);
                    } else {
                      const healthkitAuth = new HealthKitSync(user?.id || 0);
                      await healthkitAuth.authorize().then(res => {
                        if (res) {
                          setHealthKitAuthorized(true);
                          synchronizeData(UserAttributeSource.HealthKit);
                        } else {
                          setHealthKitAuthorized(false);
                        }
                        setHealthKitLoading(false);
                        checkConnectedWearables();
                      });
                    }
                  }}
                >
                  <StyledWearableView>
                    <StyledImage
                      source={require('../../assets/wearable/icon_health_kit.png')}
                    />
                    <StyledLabelContainer>
                      <StyledLabel>Apple Health</StyledLabel>
                      <StyledSubLabel
                        numberOfLines={0}
                        small={Boolean(
                          healthKitAuthorized && lastSyncedState.healthKit
                        )}
                      >
                        {healthKitAuthorized
                          ? deviceSyncInfo?.deviceKey ===
                              UserAttributeSource.HealthKit &&
                            deviceSyncInfo?.message
                            ? deviceSyncInfo?.message
                            : getDateUpdatedString(lastSyncedState.healthKit)
                          : 'Connect'}
                      </StyledSubLabel>
                    </StyledLabelContainer>
                    {healthKitLoading ? (
                      <ActivityIndicator />
                    ) : (
                      <Octicons
                        name='chevron-right'
                        size={24}
                        color='#232F55'
                      />
                    )}
                  </StyledWearableView>
                </StyledWearableContainer>
              </>
            ) : (
              <>
                <StyledWearableContainer
                  disabled={disableButtons}
                  testID={elementIDs.googleFitButtonID}
                  onPress={async () => {
                    const googleFitAuth = new GoogleFitHealth(user?.id || 0);
                    setGoogleFitLoading(true);
                    const isAuthorized = await getIsGoogleFitAuthorized();
                    if (isAuthorized) {
                      Alert.alert('Google Fit', 'Connected', [
                        {
                          text: 'Sync',
                          onPress: () => {
                            synchronizeData(UserAttributeSource.GoogleFit);
                            setGoogleFitLoading(false);
                          }
                        },
                        {
                          text: 'Disconnect',
                          onPress: async () => {
                            await googleFitAuth.disconnect();
                            await removeIsGoogleFitAuthorized();
                            setGoogleFitAuthorized(false);
                            setGoogleFitLoading(false);
                            AsyncStorage.removeItem('wearableConfigs');
                          }
                        },
                        {
                          text: 'Cancel',
                          onPress: () => {
                            setGoogleFitLoading(false);
                          }
                        }
                      ]);
                    } else {
                      await googleFitAuth.authorize().then(async () => {
                        await getIsGoogleFitAuthorized().then(value => {
                          if (value) {
                            setGoogleFitAuthorized(true);
                            setGoogleFitLoading(false);
                            synchronizeData(UserAttributeSource.GoogleFit);
                            checkConnectedWearables();
                          }
                        });
                      });
                    }
                  }}
                >
                  <StyledWearableView>
                    <StyledImage
                      source={require('../../assets/wearable/icon_google_fit.png')}
                    />
                    <StyledLabelContainer>
                      <StyledLabel>Google Fit</StyledLabel>
                      <StyledSubLabel
                        small={Boolean(
                          googleFitAuthorized && lastSyncedState.googleFit
                        )}
                      >
                        {googleFitAuthorized
                          ? deviceSyncInfo?.deviceKey ===
                              UserAttributeSource.GoogleFit &&
                            deviceSyncInfo?.message
                            ? deviceSyncInfo?.message
                            : getDateUpdatedString(lastSyncedState.googleFit)
                          : 'Connect'}
                      </StyledSubLabel>
                    </StyledLabelContainer>
                    {googleFitLoading ? (
                      <ActivityIndicator />
                    ) : (
                      <Octicons
                        name='chevron-right'
                        size={24}
                        color='#232F55'
                      />
                    )}
                  </StyledWearableView>
                </StyledWearableContainer>
                <Spacer size={3} />
                <StyledWearableContainer
                  disabled={disableButtons}
                  testID={elementIDs.healthConnectButtonID}
                  onPress={async () => {
                    setHealthConnectLoading(true);
                    const isInstalled = await Linking.canOpenURL(
                      'healthconnect://onboarding'
                    );
                    const googleHealthConnectAuth = new GoogleHealthConnect(
                      user?.id || 0
                    );
                    const syncHealthConnect = async () => {
                      const initialized =
                        await googleHealthConnectAuth.authorize();
                      if (initialized) {
                        let grantedPermissions =
                          await googleHealthConnectAuth.getGrantedPermissions();

                        const firstTimeCheck = await AsyncStorage.getItem(
                          'first-time-permission-check'
                        );
                        if (
                          grantedPermissions.length === 0 &&
                          firstTimeCheck === null
                        ) {
                          const permissions =
                            await googleHealthConnectAuth.checkPermissions();
                          if (permissions.length > 0) {
                            // request permissions seems to be causing health-connect to hang up
                            // so setting a flag to prevent it from constantly checking permissions after first time
                            await AsyncStorage.setItem(
                              'first-time-permission-check',
                              'false'
                            );
                            setHealthConnectAuthorized(true);
                          }
                        }
                      }
                      synchronizeData(UserAttributeSource.HealthConnect);
                    };
                    if (isInstalled) {
                      if (healthConnectAuthorized) {
                        Alert.alert('Health Connect', 'Connected', [
                          {
                            text: 'Sync',
                            onPress: () => {
                              synchronizeData(
                                UserAttributeSource.HealthConnect
                              );
                              setHealthConnectLoading(false);
                            }
                          },
                          {
                            text: 'Settings',
                            onPress: async () => {
                              googleHealthConnectAuth.openHealthConnectSettings();
                              setHealthConnectLoading(false);
                            }
                          },
                          {
                            text: 'Cancel',
                            onPress: () => {
                              setHealthConnectLoading(false);
                            }
                          }
                        ]);
                      } else {
                        syncHealthConnect();
                        setHealthConnectLoading(false);
                      }
                    } else {
                      Alert.alert(
                        'Health Connect',
                        'The Google Health Connect app is required to sync your health data',
                        [
                          {
                            text: 'Install',
                            onPress: async () => {
                              await Linking.openURL(
                                'market://details?id=com.google.android.apps.healthdata'
                              );
                              setHealthConnectLoading(false);
                            }
                          },
                          {
                            text: 'Cancel',
                            onPress: () => {
                              setHealthConnectLoading(false);
                            }
                          }
                        ]
                      );
                    }
                  }}
                >
                  <StyledWearableView>
                    <StyledImage
                      source={require('../../assets/wearable/icon_health_connect.png')}
                    />
                    <StyledLabelContainer>
                      <StyledLabel>Health Connect</StyledLabel>
                      <StyledSubLabel
                        small={Boolean(
                          healthConnectAuthorized &&
                          lastSyncedState.healthConnect
                        )}
                      >
                        {healthConnectAuthorized
                          ? deviceSyncInfo?.deviceKey ===
                              UserAttributeSource.HealthConnect &&
                            deviceSyncInfo?.message
                            ? deviceSyncInfo?.message
                            : getDateUpdatedString(
                                lastSyncedState.healthConnect
                              )
                          : 'Connect'}
                      </StyledSubLabel>
                    </StyledLabelContainer>
                    {healthConnectLoading ? (
                      <ActivityIndicator />
                    ) : (
                      <Octicons
                        name='chevron-right'
                        size={24}
                        color='#232F55'
                      />
                    )}
                  </StyledWearableView>
                </StyledWearableContainer>
              </>
            ))}
          {Platform.OS !== 'web' && <Spacer size={3} />}
          <>
            <StyledWearableContainer
              disabled={disableButtons}
              testID={elementIDs.ouraButtonID}
              onPress={() => {
                if (
                  (deviceSyncInfo?.status === DeviceSyncStatus.Failed &&
                    deviceSyncInfo.message === 'Oura account not connected.') ||
                  deviceSyncInfo?.message ===
                    'Oura account connection has expired.'
                ) {
                  if (user && user.ouraAccessToken) {
                    clearDeviceSyncState(UserAttributeSource.Oura);
                    setUser({ ...user, ouraAccessToken: undefined });
                    connectOura();
                    return;
                  }
                }
                if (ouraAccessToken) {
                  if (Platform.OS !== 'web') {
                    Alert.alert('Oura', 'Connected', [
                      {
                        text: 'Sync',
                        onPress: () => {
                          synchronizeData(UserAttributeSource.Oura);
                        }
                      },
                      {
                        text: 'Disconnect',
                        onPress: () => {
                          if (user) {
                            disconnectOura(user.id);
                          }
                        }
                      },
                      {
                        text: 'Cancel'
                      }
                    ]);
                  } else {
                    setShowOuraWebDialog(true);
                  }
                } else {
                  //open WeviewModal
                  if (Platform.OS === 'android') {
                    console.log('Open Webview connect');
                    setOpenAndroidAuraConnect(true);
                  } else {
                    connectOura();
                  }
                }
              }}
            >
              <StyledWearableView>
                <StyledImage
                  source={require('../../assets/wearable/icon_oura.png')}
                />
                <StyledLabelContainer>
                  <StyledLabel>Oura</StyledLabel>
                  <StyledSubLabel
                    small={Boolean(ouraAccessToken && lastSyncedState.oura)}
                  >
                    {ouraAccessToken
                      ? deviceSyncInfo?.deviceKey ===
                          UserAttributeSource.Oura && deviceSyncInfo?.message
                        ? deviceSyncInfo?.message
                        : getDateUpdatedString(lastSyncedState.oura)
                      : 'Connect'}
                  </StyledSubLabel>
                </StyledLabelContainer>
                {ouraLoading ? (
                  <ActivityIndicator />
                ) : (
                  <Octicons name='chevron-right' size={24} color='#232F55' />
                )}
              </StyledWearableView>
            </StyledWearableContainer>
          </>
          {fromMyDataScreen &&
            !route.params?.disableAutoNavigate &&
            connectedWearableSources &&
            connectedWearableSources?.length > 0 && (
              <>
                <Spacer size={4} />
                <Button
                  fullWidth
                  onPress={() => {
                    navigation.replace('DrawerNavigator', {
                      screen: 'BottomTabNav',
                      params: {
                        screen: 'MyDataNav',
                        params: { screen: 'MyDataListScreen' }
                      }
                    });
                  }}
                >
                  View Data
                </Button>
              </>
            )}
          {showFitbitWebDialog && (
            <ConfirmDialog
              confirmLabel='Sync'
              customButtonLabel='Disconnect'
              onCancelButtonClicked={() => {
                setShowFitbitWebDialog(false);
                setFitbitLoading(false);
              }}
              onConfirmButtonClicked={() => {
                synchronizeData(UserAttributeSource.Fitbit);
                setFitbitLoading(false);
                setShowFitbitWebDialog(false);
              }}
              onCustomButtonClicked={() => {
                if (user) {
                  revokeFitbitAuthorization(user.id, fitbitAuthProps.clientId)
                    ?.then(() => {
                      setFitbitAccessToken(undefined);
                      setFitbitLoading(false);
                      updateUserSession();
                    })
                    ?.catch(() => {
                      setFitbitAccessToken(undefined);
                      setFitbitLoading(false);
                      updateUserSession();
                    });
                  setShowFitbitWebDialog(false);
                }
              }}
              title={'Fitbit'}
              message={'Connected'}
            />
          )}
          {showOuraWebDialog && (
            <ConfirmDialog
              confirmLabel='Sync'
              customButtonLabel='Disconnect'
              onCancelButtonClicked={() => {
                setShowOuraWebDialog(false);
              }}
              onConfirmButtonClicked={() => {
                synchronizeData(UserAttributeSource.Oura);
                setShowOuraWebDialog(false);
              }}
              onCustomButtonClicked={async () => {
                if (user) {
                  await disconnectOura(user.id);
                  setShowOuraWebDialog(false);
                }
              }}
              title={'Oura'}
              message={'Connected'}
            />
          )}
        </StyledContentContainer>
      </StyledScrollView>
      {openAndroidOuraConnect && (
        <WebViewModal
          height={900}
          incognito={true}
          scrollOnFieldClicked
          url={ouraAuthUrl}
          onClose={() => {
            setOpenAndroidAuraConnect(false);
          }}
          onNavigationStateChange={stateNav => {
            if (stateNav.url.includes(config.redirectUri)) {
              handleOuraAuthResult(stateNav);
              setOpenAndroidAuraConnect(false);
            }
          }}
          title={'Connect Oura'}
        />
      )}
    </StyledContainer>
  );
};

export default MyProfileWearableSettingsScreen;
